import { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../../shared/utils/request";
import { Toaster, toast } from "react-hot-toast";

const AddressModal = ({ show, onClose, editingAddress, onSaveAddress }) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [mapCoords, setMapCoords] = useState({
    lat: 10.026764082572074,
    lng: 76.30081939683588,
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const validationSchema = Yup.object({
    name: Yup.string().required("Full name is required"),
    address: Yup.string().required("Address is required"),
    phone: Yup.string()
      .required("Phone number is required")
      .matches(/^[0-9]{10}$/, "Phone number must be 10 digits"),
    city: Yup.string().required("City is required"),
    postcode: Yup.string().required("Post code is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      address: "",
      phone: "",
      city: "",
      postcode: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSaveLoading(true);
        const data = await api.post("customer/addresses", {
          name: values?.name,
          address_line_1: values?.address,
          city: values?.city,
          phone: values?.phone,
          state: "Kerala",
          pincode: values?.postcode,
          // latitude: mapCoords?.lat,
          // longitude: mapCoords?.lng,
          latitude: 10.026764082572074,
          longitude: 76.30081939683588,
          is_default: true,
        });
        if (data?.status === false) {
          toast.error(data?.message);
        } else {
          onSaveAddress(values);
          formik.resetForm();
          onClose();
        }
        setSaveLoading(false);
      } catch (error) {
        console.error("Failed to fetch cart data:", error);
      } finally {
        setSaveLoading(false);
      }
    },
  });

  const getCurrentLocation = () => {
    setLoadingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setMapCoords({ lat: latitude, lng: longitude });

          fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
          )
            .then((res) => res.json())
            .then((data) => {
              if (data?.address) {
                formik.setFieldValue(
                  "city",
                  data.address.suburb ||
                    data.address.hamlet ||
                    data.address.village ||
                    "",
                );
                formik.setFieldValue("postcode", data.address.postcode || "");
              }
              if (data.display_name) {
                setCurrentLocation(data.display_name);
                formik.setFieldValue("address", data.display_name);
              }
              setLoadingLocation(false);
            })
            .catch(() => {
              setLoadingLocation(false);
              alert("Unable to fetch address");
            });
        },
        () => {
          alert(
            "Unable to retrieve your location. Please enable location access.",
          );
          setLoadingLocation(false);
        },
      );
    } else {
      alert("Geolocation is not supported by your browser");
      setLoadingLocation(false);
    }
  };

  useEffect(() => {
    if (show && window.google) {
      const map = new window.google.maps.Map(document.getElementById("map"), {
        center: mapCoords,
        zoom: 15,
      });

      const marker = new window.google.maps.Marker({
        position: mapCoords,
        map: map,
        draggable: true,
      });

      marker.addListener("dragend", (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setMapCoords({ lat, lng });

        fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        )
          .then((res) => res.json())
          .then((data) => {
            if (data?.address) {
              formik.setFieldValue(
                "city",
                data.address.suburb ||
                  data.address.hamlet ||
                  data.address.village ||
                  "",
              );
              formik.setFieldValue("postcode", data.address.postcode || "");
            }
            if (data.display_name) {
              setCurrentLocation(data.display_name);
              formik.setFieldValue("address", data.display_name);
            }
          });
      });

      mapRef.current = map;
      markerRef.current = marker;
    }
  }, [show, mapCoords]);

  useEffect(() => {
    if (show) {
      if (editingAddress) {
        formik.setValues({
          name: editingAddress.name || "",
          address: editingAddress.address || "",
          phone: editingAddress.phone || "",
          city: editingAddress.city || "",
          postcode: editingAddress.postcode || "",
        });
      } else {
        formik.resetForm();
        setCurrentLocation(null);
        getCurrentLocation();
      }
    }
  }, [show, editingAddress]);

  const inputProps = (field) => ({
    type: field === "phone" ? "number" : "text",
    name: field,
    value: formik.values[field],
    onChange: formik.handleChange,
    className: `common-input style-two rounded-10 ${
      formik.touched[field] && formik.errors[field]
        ? "border-danger"
        : "border-gray-200"
    }`,
    placeholder: placeholders[field],
    style: { paddingLeft: "40px" },
  });

  const placeholders = {
    name: "Enter full name",
    address: "House address, number and street name",
    phone: "Enter phone number",
    city: "Enter city",
    postcode: "Enter post code",
  };

  const fieldIcons = {
    name: "ph-user",
    address: "ph-house",
    phone: "ph-phone",
    city: "ph-city",
    postcode: "ph-hash",
  };

  const inputError = (field) =>
    formik.touched[field] && formik.errors[field] ? (
      <p className="text-danger text-xs mt-1 d-flex align-items-center gap-4">
        <i className="ph ph-warning-circle" />
        {formik.errors[field]}
      </p>
    ) : null;

  const renderField = (field, label, colClass) => (
    <div className={colClass}>
      <label className="text-sm fw-semibold text-gray-700 mb-8 d-block">
        {label}
        <span className="text-danger"> *</span>
      </label>
      <div className="position-relative">
        <i
          className={`ph ${fieldIcons[field]} position-absolute text-main-600`}
          style={{ left: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "16px" }}
        />
        <input {...inputProps(field)} />
      </div>
      {inputError(field)}
    </div>
  );

  if (!show) return null;

  return (
    <>
      <script
        src="https://maps.googleapis.com/maps/api/js?key=AIzaSyBR5HGnPOjkg9PHCQZMfi7KbU2PppAIIB4XXXXX"
        async
        defer
      ></script>
      <style>{`
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div
        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-end align-items-md-center justify-content-center"
        style={{ zIndex: 9999, background: "rgba(0,0,0,0.5)" }}
        onClick={onClose}
      >
        <Toaster position="top-center" />
        <div
          className="bg-white shadow-lg position-relative d-flex flex-column"
          style={{
            width: "100%",
            maxWidth: "700px",
            maxHeight: "92dvh",
            borderRadius: "20px 20px 0 0",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="d-flex align-items-center justify-content-between px-16 px-md-24 py-14 flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #1a6b30 0%, #228b34 50%, #2ea44f 100%)",
              borderRadius: "20px 20px 0 0",
            }}
          >
            <div className="d-flex align-items-center gap-10">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle"
                style={{ width: "38px", height: "38px", background: "rgba(255,255,255,0.2)" }}
              >
                <i className="ph-fill ph-map-pin text-white" style={{ fontSize: "20px" }} />
              </div>
              <div>
                <h5 className="text-white fw-bold mb-0" style={{ fontSize: "1.05rem" }}>
                  {editingAddress ? "Edit Address" : "Add New Address"}
                </h5>
                <p className="text-white mb-0" style={{ fontSize: "11px", opacity: 0.85 }}>
                  Fill in your delivery details
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="bg-transparent border-0 text-white d-flex align-items-center justify-content-center rounded-circle"
              style={{ width: "34px", height: "34px", opacity: 0.9 }}
            >
              <i className="ph-bold ph-x text-xl" />
            </button>
          </div>

          {/* Body */}
          <div className="px-16 px-md-24 py-16 overflow-y-auto" style={{ flex: 1 }}>
            {/* Map + Location */}
            <div className="mb-20">
              <div
                id="map"
                className="rounded-12 border border-main-100 w-100"
                style={{ height: "220px" }}
              ></div>
              <p className="text-xs text-gray-600 mt-8 mb-0">
                <i className="ph ph-info me-1" />
                Drag the marker to adjust your location
              </p>
            </div>

            <button
              onClick={getCurrentLocation}
              disabled={loadingLocation}
              className="btn btn-outline-main w-100 mb-20 py-12 rounded-pill d-flex align-items-center justify-content-center gap-8"
              style={{ transform: "none" }}
            >
              <i
                className={`ph-bold ${loadingLocation ? "ph-spinner animate-spin" : "ph-crosshair"}`}
                style={{ fontSize: "16px" }}
              />
              {loadingLocation ? "Getting Location..." : "Use Current Location"}
            </button>

            {currentLocation && (
              <div
                className="d-flex align-items-center gap-6 px-12 py-10 mb-20 rounded-10"
                style={{ background: "#e8f5e9", border: "1px solid #a5d6a7", color: "#166534", fontSize: "12.5px" }}
              >
                <i className="ph-fill ph-check-circle flex-shrink-0" />
                <span className="text-truncate">{currentLocation}</span>
              </div>
            )}

            <form id="address-form" onSubmit={formik.handleSubmit} noValidate>
              <div className="row gy-3">
                {renderField("name", "Full Name", "col-12")}
                {renderField("address", "Home Address", "col-12")}
                {renderField("phone", "Phone", "col-12")}
                {renderField("city", "City", "col-sm-6")}
                {renderField("postcode", "Post Code", "col-sm-6")}
              </div>
            </form>
          </div>

          {/* Sticky Footer Actions */}
          <div
            className="px-16 px-md-24 py-12 flex-shrink-0 d-flex gap-10 flex-wrap"
            style={{ borderTop: "1px solid #eef5f0", background: "#fff", borderRadius: "0 0 20px 20px" }}
          >
            <button
              type="submit"
              form="address-form"
              disabled={saveLoading}
              className="btn btn-main flex-grow-1 d-inline-flex align-items-center justify-content-center gap-8 rounded-pill fw-bold py-12"
              style={{ transform: "none", flexBasis: "150px" }}
            >
              {saveLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="ph ph-check-circle" />
                  Save Address
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline-main flex-grow-1 d-inline-flex align-items-center justify-content-center gap-8 rounded-pill fw-bold py-12"
              style={{ transform: "none", flexBasis: "150px" }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddressModal;
